<template>
  <div class="grid grid-cols-1 gap-7">
    <h5 class="font-bold">Feedback</h5>
    <p class="paragraph-1">
      At Otivo we value your opinion and would love to hear your genuine feedback. Please feel free
      to talk about any bugs and suggestions.
    </p>
    <form class="flex flex-col gap-7" @submit.prevent="handleSubmit">
      <!-- Rating Question -->
      <div class="flex flex-col gap-3">
        <label class="font-medium text-gray-700 text-sm">
          Rate your experience with Otivo so far? <span class="text-secondary">*</span>
        </label>
        <div class="flex gap-3 items-center">
          <button
            v-for="rating in ratings"
            :key="rating.value"
            class="bg-transparent border-none p-1 rounded-full cursor-pointer transition-all duration-200 flex items-center justify-center hover:bg-grey-4 hover:fill-primary group"
            type="button"
            @click="setRating(rating.value)">
            <component
              :is="rating.icon"
              :class="formData.rating === rating.value ? 'fill-primary' : 'fill-gray-400'"
              class="w-8 h-8 transition-all duration-200 group-hover:fill-blue-2" />
          </button>
        </div>
      </div>

      <!-- Support Session Question -->
      <div class="flex flex-col gap-3">
        <label class="font-medium text-gray-700 text-sm"
          >Would you be open to a feedback session with our customer support team?
          <span class="text-secondary">*</span></label
        >
        <div class="flex gap-7">
          <label class="flex items-center gap-2 cursor-pointer text-sm">
            <input
              v-model="formData.supportSession"
              class="w-4 h-4 accent-blue-500"
              name="supportSession"
              type="radio"
              value="yes" />
            <span class="text-gray-700">Yes</span>
          </label>
          <label class="flex items-center gap-2 cursor-pointer text-sm">
            <input
              v-model="formData.supportSession"
              class="w-4 h-4 accent-blue-500"
              name="supportSession"
              type="radio"
              value="no" />
            <span class="text-gray-700">No</span>
          </label>
        </div>
      </div>

      <!-- Feedback Type Field -->
      <div class="flex flex-col gap-3">
        <label class="font-medium text-gray-700 text-sm" for="subject"
          >Subject <span class="text-secondary">*</span></label
        >
        <Dropdown
          :existing-item="null"
          :items="subjectOptions"
          placeholder="Select"
          type="feedback-type"
          @selected="handleSubjectSelected" />
      </div>

      <!-- Feedback Textarea -->
      <div class="flex flex-col gap-3">
        <label class="font-medium text-gray-700 text-sm" for="feedback">
          Message <span class="text-secondary">*</span>
          <span class="text-xs text-gray-500 font-normal">(min 10, max 5000 characters)</span>
        </label>
        <BaseTextArea
          v-model:value="formData.feedback"
          :class="[
            'rounded-md text-sm font-[inherit] resize-y min-h-24 transition-all duration-200 focus:outline-hidden focus:ring-2 placeholder-gray-400',
            isFeedbackValid
              ? 'border-gray-300 focus:border-blue-500 focus:ring-blue-100'
              : 'border-red-300 focus:border-red-500 focus:ring-red-100',
          ]"
          name="feedback"
          placeholder="Enter your feedback here..."
          required />
        <div class="flex justify-between items-center text-xs">
          <span :class="isFeedbackValid ? 'text-gray-500' : 'text-red-500'">
            {{ feedbackCharCount }}/5000 characters
            <span v-if="feedbackCharCount < 10" class="text-red-500">
              ({{ 10 - feedbackCharCount }} more needed)
            </span>
          </span>
          <span v-if="feedbackCharRemaining < 100" class="text-amber-600">
            {{ feedbackCharRemaining }} remaining
          </span>
        </div>
      </div>

      <!-- File Upload Section -->
      <FileUpload
        v-if="uploadsAllowed"
        v-model="formData.attachedFiles"
        :allowed-types="['image/*']"
        :max-file-size="20 * 1024 * 1024"
        :max-files="10"
        button-text="Attach files"
        help-text="Attach up to 10 files. The max allowed size per file is 20 MB."
        @error="handleFileUploadError"
        @files-changed="handleFilesChanged" />

      <!-- Error Message -->
      <div v-if="submitError" class="bg-red-50 border border-red-200 rounded-md p-3">
        <p class="text-red-800 text-sm">{{ submitError }}</p>
      </div>

      <!-- Success Message -->
      <div v-if="submitSuccess" class="bg-green-50 border border-green-200 rounded-md p-3">
        <p class="text-green-800 text-sm">Thank you for your feedback! We appreciate your input.</p>
      </div>

      <!-- Submit Button -->
      <OtivoButton
        :disabled="!isFormValid || isSubmitting"
        class="*:px-8 self-center hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
        type="submit">
        <span v-if="isSubmitting">Submitting...</span>
        <span v-else>Send feedback</span>
      </OtivoButton>
    </form>
  </div>
</template>

<script lang="ts" setup>
import { defineAsyncComponent } from 'vue'
import OtivoButton from '@/components/OtivoButton.vue'
import BaseTextArea from '@/components/Inputs/BaseTextArea.vue'
import Dropdown from '@/components/BasicInputs/Dropdown.vue'
import FileUpload from '@/components/BasicInputs/FileUpload.vue'
import { useFeedbackForm } from '@/composables/useFeedbackForm'

// Import feedback rating icons
const Feedback1 = defineAsyncComponent(
  () => import('@/components/SVGS/BrandIcons/feedback/feedback1.svg')
)
const Feedback2 = defineAsyncComponent(
  () => import('@/components/SVGS/BrandIcons/feedback/feedback2.svg')
)
const Feedback3 = defineAsyncComponent(
  () => import('@/components/SVGS/BrandIcons/feedback/feedback3.svg')
)
const Feedback4 = defineAsyncComponent(
  () => import('@/components/SVGS/BrandIcons/feedback/feedback4.svg')
)
const Feedback5 = defineAsyncComponent(
  () => import('@/components/SVGS/BrandIcons/feedback/feedback5.svg')
)

// Rating options configuration
const ratings = [
  { value: 1, icon: Feedback1 },
  { value: 2, icon: Feedback2 },
  { value: 3, icon: Feedback3 },
  { value: 4, icon: Feedback4 },
  { value: 5, icon: Feedback5 },
]

// Use the feedback form composable
const {
  formData,
  subjectOptions,
  uploadsAllowed,
  isSubmitting,
  submitError,
  submitSuccess,
  isFormValid,
  feedbackCharCount,
  feedbackCharRemaining,
  isFeedbackValid,
  setRating,
  handleSubjectSelected,
  handleFilesChanged,
  handleSubmit,
  resetForm,
} = useFeedbackForm(false)

// File upload error handler
const handleFileUploadError = (errors: string[]) => {
  // Handle file upload errors (could show toast, etc.)
  console.error('File upload errors:', errors)
}
</script>
